import { fg } from '@atlaskit/platform-feature-flags/fg';

const DEFAULT_EXPIRATION = 1000 * 60 * 15; // 15 mins in milliseconds
const PERSISTED_EXPIRATION_GRACE_PERIOD = 1000 * 60 * 60 * 24;

export interface ICache {
	/**
	 * In ms determine the duration for which the entry should be valid.
	 */
	expiration?: number;
}

class Cache<T> {
	private map: Record<string, Entry<T>>;
	private expiration: number;

	constructor(args: ICache = {}) {
		const { expiration = DEFAULT_EXPIRATION } = args;
		this.map = {};
		this.expiration = expiration;
	}

	public get(key: string, returnExpired: boolean = false): Promise<T> | undefined {
		const entry = this.map[key];

		if (entry) {
			if (entry.isExpired()) {
				delete this.map[key];
				return returnExpired ? entry.getResult() : undefined;
			} else {
				return entry.getResult();
			}
		}

		return;
	}

	public set(key: string, result: Promise<T>, expiration?: number): void {
		const entry = new Entry({
			result,
			expirationTime: Date.now() + (expiration || this.expiration),
			accessCount: 0,
		});

		this.map[key] = entry;
	}

	public has(key: string): boolean {
		return key in this.map;
	}

	public clear(key?: string): void {
		if (key) {
			delete this.map[key];
		} else {
			this.map = {};
		}
	}

	public async serialize(): Promise<string> {
		const serialized: Record<string, IEntry<T>> = {};
		const cleanupEnabled = fg('search_cache_cleanup_on_write');
		const cutoff = Date.now() - PERSISTED_EXPIRATION_GRACE_PERIOD;
		const resolvers = Object.entries(this.map).map(([key, entry]) =>
			(async () => {
				// Filter persistence only: stale responses remain available in memory.
				// Skip before awaiting so obsolete promises cannot block this write.
				if (cleanupEnabled && entry.getExpirationTime() < cutoff) {
					return;
				}
				serialized[key] = {
					result: await entry.getResult(),
					expirationTime: entry.getExpirationTime(),
					accessCount: entry.getAccessCount(),
				};
			})(),
		);
		await Promise.all(resolvers);
		return JSON.stringify(serialized);
	}

	public deserialize(persisted: string): Record<string, IEntry<T>> | undefined {
		try {
			const object = JSON.parse(persisted);

			for (const key in object) {
				const { result, expirationTime, accessCount }: IEntry<T> = object[key];

				this.map[key] = new Entry({
					result: Promise.resolve(result),
					expirationTime,
					accessCount,
				});
			}

			return object;
		} catch {
			// TODO: Log error via sentry
		}
		return undefined;
	}

	public isExpired(key: string): boolean {
		const entry = this.map[key];
		return entry ? entry.isExpired() : true;
	}

	public getRemainingTtl(key: string): number | undefined {
		const entry = this.map[key];
		if (!entry) {
			return undefined;
		}
		return Math.max(0, entry.getExpirationTime() - Date.now());
	}
}

interface IEntry<T> {
	result: T;
	expirationTime: number;
	accessCount: number;
}

class Entry<T> {
	private result: Promise<T>;
	private expirationTime: number;
	private accessCount: number;

	constructor({ result, expirationTime, accessCount }: IEntry<Promise<T>>) {
		this.result = result;
		this.expirationTime = expirationTime;
		this.accessCount = accessCount;
	}

	public getResult() {
		this.accessCount++;
		return this.result;
	}

	public isExpired() {
		return this.expirationTime < Date.now();
	}

	public getExpirationTime() {
		return this.expirationTime;
	}

	public getAccessCount() {
		return this.accessCount;
	}
}

export const getInstance: <T>(args?: ICache) => Cache<T> = <T>(args?: ICache) => {
	return new Cache<T>(args);
};
